import {
  Component,
  effect,
  inject,
  Injector,
  ViewChild,
  ViewContainerRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  ModalConfig,
  ModalReference,
  ModalResult,
  ModalService,
} from '../../../core/services/modal.service';
import { DynamicModalDialogComponent } from './dynamic-modal-dialog.component';

/**
 * Root container component for dynamic modals.
 * Lives in app.html as a single global instance (like ToastComponent).
 *
 * Responsibilities:
 * - Listens to ModalService signals
 * - Creates/destroys DynamicModalDialogComponent
 * - Dynamically injects the user's component into the dialog content
 * - Manages ViewContainerRef for dynamic component creation
 */
@Component({
  selector: 'app-modal-container',
  standalone: true,
  imports: [DynamicModalDialogComponent],
  templateUrl: './modal-container.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./modal-container.component.css'],
})
export class ModalContainerComponent {
  @ViewChild('dynamicContent', { read: ViewContainerRef })
  dynamicContentRef!: ViewContainerRef;

  protected service = inject(ModalService);
  private injector = inject(Injector);
  private lastProcessedCloseConfig: any = null;

  constructor() {
    // Watch for modal open/close and render or clear component
    effect(() => {
      const config = this.service.config();
      if (config) {
        // Reset lastProcessedCloseConfig for new modal to track its closeSignal independently
        this.lastProcessedCloseConfig = null;
        // Use setTimeout to allow ViewChild to be available after @if renders
        setTimeout(() => this.renderModalComponent(config));
      } else {
        this.clearModalComponent();
      }
    });

    // Watch for closeSignal changes and close the modal when it's set
    // This is the signal-based closure flow:
    // 1. Host component sets closeSignal to trigger closure
    // 2. Effect watches for changes (non-null values)
    // 3. Container calls service.closeModal() to complete the closure
    effect(() => {
      const config = this.service.config();
      if (config?.closeSignal) {
        const closeConfig = config.closeSignal();
        // Only process if signal is not null AND it's a new value (not same as last time)
        if (closeConfig && closeConfig !== this.lastProcessedCloseConfig) {
          this.lastProcessedCloseConfig = closeConfig;
          // Call service.closeModal with the appropriate ModalResult
          this.service.closeModal({
            action: closeConfig.action,
            reason: closeConfig.reason as any,
            data: closeConfig.data,
          });
        }
      }
    });
  }

  private renderModalComponent(config: ModalConfig): void {
    if (!this.dynamicContentRef) {
      return;
    }

    // Clear any previous component
    this.dynamicContentRef.clear();

    // Create a ModalReference that delegates to the service
    const modalRef = this.createModalReference();

    // Create the component with an injector that provides ModalReference
    const modalInjector = Injector.create({
      parent: this.injector,
      providers: [
        {
          provide: ModalReference,
          useValue: modalRef,
        },
      ],
    });

    const componentRef = this.dynamicContentRef.createComponent(config.component, {
      injector: modalInjector,
    });

    // Set inputs on the component instance
    if (config.componentInputs) {
      Object.entries(config.componentInputs).forEach(([key, value]) => {
        (componentRef.instance as any)[key] = value;
      });
    }

    // Register the component instance with the service
    this.service.setComponentInstance(componentRef.instance);

    // Trigger change detection on the created component
    componentRef.changeDetectorRef.detectChanges();
  }

  private createModalReference(): ModalReference {
    // Create a concrete ModalReference implementation
    const service = this.service;

    return {
      close<T = any>(result?: ModalResult<T>): void {
        service.closeModal(result);
      },
      getComponent(): any {
        return service.getComponentInstance();
      },
      isOpen(): boolean {
        return service.isModalOpen();
      },
      getConfig(): ModalConfig {
        return service.getCurrentConfig()!;
      },
    } as ModalReference;
  }

  private clearModalComponent(): void {
    if (this.dynamicContentRef) {
      this.dynamicContentRef.clear();
    }
  }

  /**
   * Handle close from the dialog component (header close button, escape, etc.)
   */
  onDialogClose(result?: ModalResult): void {
    const config = this.service.config();
    if (result?.reason === 'header_close_button' && config?.onHeaderCloseClick) {
      config.onHeaderCloseClick();
      return;
    }
    if (result?.reason === 'backdrop_click' && config?.onClickOutside) {
      config.onClickOutside();
      return;
    }
    if (config?.onClose) {
      config.onClose();
    }
    this.service.closeModal(result ?? { action: 'close', reason: 'header_close_button' });
  }

  /**
   * Handle cancel from the dialog component
   */
  onDialogCancel(result?: ModalResult): void {
    const config = this.service.config();
    if (config?.onCancel) {
      config.onCancel();
    }
    this.service.closeModal(result ?? { action: 'cancel' });
  }
}
